import jwtDecode from 'jwt-decode';

import { isAccessTokenExpired } from './authTokenExpiry';
import type { RootState } from './redux/store';

// Re-exported from its leaf module so existing importers (components/AuthBootstrapGate) keep
// working; the definition lives in authTokenExpiry to stay free of the redux/store import.
export { isAccessTokenExpired };

/**
 * Does the user have an authenticated SESSION, for routing purposes? True when a user with a
 * decodable access token is present — even if that 15-minute access token has already expired.
 *
 * The route guard (routes/authRoute.tsx) must NOT log out on mere access-token expiry: the
 * access token is short-lived by design, and the httpOnly refresh cookie still mints a fresh
 * one (AuthBootstrapGate on load, baseApi on the next XHR 401). The server enforces the JWT
 * `exp` on every request, so an expired token grants no access — it just triggers a silent
 * refresh. Treating expiry as "logged out" here is what produced the ~15-minute logout loop:
 * the guard re-evaluates on every client-side navigation and the bootstrap gate only covers
 * a hard reload. The single authority that ends a session is baseApi's signOut when a refresh
 * actually fails; that clears `user`, after which this returns false and the guard redirects.
 */
export const checkIfTokenIsValid = (user: RootState['user'] | null): boolean => {
  if (!user?.accessToken) {
    return false;
  }
  try {
    // Decodable JWT == a real session. A garbage/non-JWT token is not.
    jwtDecode(user.accessToken);
    return true;
  } catch (_error) {
    return false;
  }
};
